// src/shared/ui/StatusBadge.jsx
//
// One badge component to replace the 7 near-identical status/priority pill
// implementations that were scattered across modules:
//   components/StatusBadge.jsx            (coverage)
//   IT/components/StatusPill.jsx           (it request status)
//   golive/components/RunStatusBadge.jsx   (deployment run status)
//   compliance/components/JobBadges.jsx    (job status + job type)
//   adai/components/history/StatusBadge.jsx(doc-gen job status)
//   rds_ui/components/common/StatusBadge.jsx (generation/SOW status)
//   rds_ui/components/common/PriorityBadge.jsx (risk/artifact priority)
//
// Usage:
//   <StatusBadge map="golive" status={run.status} />
//   <StatusBadge map="priority" status={risk.priority} />
//   <StatusBadge map={customMap} status={status} size="lg" pulse />
//
// `map` can be either the name of a registered map (see statusMaps.js) or
// an inline map object, so a module can still define a one-off vocabulary
// without writing a whole new badge component.

import PropTypes from "prop-types";
import {
  STATUS_MAP_REGISTRY,
  normalizeStatusKey,
} from "./statusMaps";

const SIZE_CLASSES = {
  sm: "px-2 py-0.5 text-xs gap-1",
  md: "px-2.5 py-1 text-xs gap-1.5",
  lg: "px-3 py-1.5 text-sm gap-2",
};

const ICON_SIZES = { sm: 11, md: 13, lg: 16 };

const DEFAULT_CLASSNAME = "bg-gray-100 text-gray-500 border border-gray-200";

function resolveEntry(map, status) {
  if (!map) return null;
  if (map[status]) return map[status];

  // Fall back to normalized matching (handles "Partially Covered" /
  // "partially-covered" / "partially_covered" all hitting the same entry,
  // the way the old coverage badge did).
  const normalized = normalizeStatusKey(status);
  const hit = Object.keys(map).find((k) => normalizeStatusKey(k) === normalized);
  return hit ? map[hit] : null;
}

export default function StatusBadge({ map, status, size = "md", pulse = false, className = "" }) {
  const resolvedMap = typeof map === "string" ? STATUS_MAP_REGISTRY[map] : map;
  const entry = resolveEntry(resolvedMap, status);

  const label = entry?.label ?? (status || "—");
  const Icon = entry?.icon;
  const badgeClassName = entry?.className ?? DEFAULT_CLASSNAME;
  const legacyColorClass = entry?.color; // for modules still on hand-written CSS (IT's it-pill)

  const isPulsing = pulse;

  if (legacyColorClass) {
    // Preserve the old hand-written-CSS look (e.g. IT's `it-pill it-{status}`)
    // for modules that haven't migrated their stylesheet yet.
    return <span className={`it-pill ${legacyColorClass} ${className}`.trim()}>{label}</span>;
  }

  return (
    <span
      className={[
        "inline-flex items-center rounded-full font-semibold whitespace-nowrap border border-transparent",
        SIZE_CLASSES[size] ?? SIZE_CLASSES.md,
        badgeClassName,
        className,
      ].filter(Boolean).join(" ")}
    >
      {isPulsing && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {Icon && <Icon size={ICON_SIZES[size] ?? ICON_SIZES.md} />}
      {label}
    </span>
  );
}

StatusBadge.propTypes = {
  /** Name of a map registered in statusMaps.js, or an inline map object */
  map: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  status: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  pulse: PropTypes.bool,
  className: PropTypes.string,
};
